/**
 * useGenerationSubmit
 *
 * Returns an onSubmit handler for GenerationFormV2.
 * Max 50 lines.
 */
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { GenerateImageV2Request } from '@/lib/api/generation';
import { formatNumber } from '@/lib/utils';

interface FormData {
  prompt_input: string; ratio: string; resolution: string; preset_type: string;
  delivery_method: 'wait' | 'mail'; image_count: string; image_format: string;
  filename_prefix: string; filename_suffix: string; png_quality: string;
  gen_quality: string; visible_watermark_enabled: boolean; visible_watermark_position: string;
}

interface Options {
  effectiveFile: File | null | undefined;
  creditsPerImage: number;
  hasEnoughCredits: boolean;
  cost: number;
  credits: number;
  selectedBrandId: string | null;
  referencedAssetIdsRef: React.MutableRefObject<string[]>;
  selectedModelCode?: string | null;
  referenceImageIds?: Array<string | null>;
  onGenerationStart?: () => void;
  mutate: (args: GenerateImageV2Request) => void;
}

export function useGenerationSubmit(opts: Options) {
  const { t } = useTranslation();

  return (data: FormData) => {
    const { effectiveFile, creditsPerImage, hasEnoughCredits, cost, credits, selectedBrandId, referencedAssetIdsRef, selectedModelCode, referenceImageIds, onGenerationStart, mutate } = opts;
    if (!effectiveFile) { toast.error(t('generation.selectImageError')); return; }
    if (!creditsPerImage) { toast.error(t('errors.pricingLoad')); return; }
    if (!hasEnoughCredits) {
      toast.error(t('generation.notEnoughCreditsMsg', { cost: formatNumber(cost), credits: credits != null ? formatNumber(credits) : '?' }));
      return;
    }
    onGenerationStart?.();
    const filteredRefIds = (referenceImageIds ?? []).filter((id): id is string => id !== null);
    mutate({
      image: effectiveFile, prompt_input: data.prompt_input, ratio: data.ratio, resolution: data.resolution,
      custom_instruction_id: data.preset_type.startsWith('custom:') ? data.preset_type.replace('custom:', '') : undefined,
      delivery_method: data.delivery_method, image_count: data.image_count, brand_id: selectedBrandId || undefined,
      referenced_asset_ids: referencedAssetIdsRef.current.length > 0 ? referencedAssetIdsRef.current : undefined,
      model: selectedModelCode ?? undefined,
      gen_quality: data.gen_quality || undefined,
      reference_image_ids: filteredRefIds.length > 0 ? filteredRefIds : undefined,
      visible_watermark_enabled: data.visible_watermark_enabled || undefined,
      visible_watermark_position: data.visible_watermark_enabled ? data.visible_watermark_position : undefined,
      image_format: data.image_format || undefined,
      png_quality: data.png_quality || undefined,
      filename_prefix: data.filename_prefix || undefined,
      filename_suffix: data.filename_suffix || undefined,
    });
  };
}
